import { pgTable, text, boolean, timestamp, integer, bigint, index, foreignKey, unique, pgEnum } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { manager, adminUser } from './admin';
import { chat } from './chat';
import { order } from './commerce';

// Enum для статуса авторизации MTProto
export const mtprotoAuthStatus = pgEnum('MtprotoAuthStatus', [
  'NOT_STARTED',
  'WAITING_CODE',
  'WAITING_PASSWORD',
  'WAITING_QR',
  'AUTHORIZED',
  'EXPIRED',
  'ERROR',
]);

// ==========================================
// MANAGER TELEGRAM SESSION - расширенные данные сессии MTProto
// Поддерживает несколько аккаунтов на одного админа
// ==========================================

export const managerTelegramSession = pgTable('ManagerTelegramSession', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  managerId: integer().notNull(),

  // Данные авторизации (зашифрованы AES-256-GCM)
  encryptedSession: text(),
  sessionSalt: text(),

  // Метаданные Telegram аккаунта
  telegramUserId: bigint({ mode: 'number' }),
  telegramUsername: text(),
  telegramPhone: text(),
  telegramFirstName: text(),
  telegramLastName: text(),
  telegramPhotoUrl: text(),

  // Статус и активность
  authStatus: mtprotoAuthStatus().default('NOT_STARTED').notNull(),
  isActive: boolean().default(false).notNull(), // Активная сессия для этого менеджера
  lastSyncAt: timestamp({ precision: 3, mode: 'date' }),
  lastErrorMessage: text(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  // Уникальность: один telegramUserId на одного менеджера (нельзя добавить один и тот же аккаунт дважды)
  unique('ManagerTelegramSession_managerId_telegramUserId_key').on(table.managerId, table.telegramUserId),
  index('ManagerTelegramSession_managerId_idx').using('btree', table.managerId.asc().nullsLast()),
  index('ManagerTelegramSession_authStatus_idx').using('btree', table.authStatus.asc().nullsLast()),
  index('ManagerTelegramSession_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  foreignKey({
    columns: [table.managerId],
    foreignColumns: [manager.id],
    name: 'ManagerTelegramSession_managerId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// TELEGRAM MESSAGE - кэш сообщений для поиска и публичности
// ==========================================

export const telegramMessage = pgTable('TelegramMessage', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  chatId: integer().notNull(),
  telegramMessageId: bigint({ mode: 'number' }).notNull(),

  // Содержимое
  text: text(),
  mediaType: text(),
  mediaUrl: text(),
  replyToMessageId: bigint({ mode: 'number' }),

  // Форматирование текста (entities из Telegram API)
  entities: text(), // JSON массив MessageEntity [{type, offset, length, url?, ...}]

  // Медиа файлы (для полного сохранения)
  fileId: text(),           // Telegram file_id/file_unique_id для повторного доступа
  fileName: text(),         // Название файла
  fileSize: bigint({ mode: 'number' }),      // Размер в байтах
  fileMimeType: text(),     // MIME тип

  // Стикеры
  stickerEmoji: text(),     // Эмодзи стикера
  stickerSetName: text(),   // Название стикерпака

  // Голосовые/видео сообщения
  mediaDuration: integer(), // Длительность в секундах

  // Метаданные отправителя
  fromId: bigint({ mode: 'number' }).notNull(),
  fromName: text(),
  fromUsername: text(),     // @username отправителя
  isOutgoing: boolean().default(false).notNull(),

  // Публичность для других админов
  isPublic: boolean().default(false).notNull(),
  markedPublicByAdminId: integer(),
  markedPublicAt: timestamp({ precision: 3, mode: 'date' }),

  // Timestamps
  telegramDate: timestamp({ precision: 3, mode: 'date' }).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  unique('TelegramMessage_chatId_telegramMessageId_key').on(table.chatId, table.telegramMessageId),
  index('TelegramMessage_chatId_idx').using('btree', table.chatId.asc().nullsLast()),
  index('TelegramMessage_isPublic_idx').using('btree', table.isPublic.asc().nullsLast()),
  index('TelegramMessage_telegramDate_idx').using('btree', table.telegramDate.desc().nullsLast()),
  foreignKey({
    columns: [table.chatId],
    foreignColumns: [chat.id],
    name: 'TelegramMessage_chatId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.markedPublicByAdminId],
    foreignColumns: [adminUser.id],
    name: 'TelegramMessage_markedPublicByAdminId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// MESSAGE ANNOTATION - заметки админов к сообщениям
// ==========================================

export const messageAnnotation = pgTable('MessageAnnotation', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  messageId: integer().notNull(),
  adminUserId: integer().notNull(),

  content: text().notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  index('MessageAnnotation_messageId_idx').using('btree', table.messageId.asc().nullsLast()),
  foreignKey({
    columns: [table.messageId],
    foreignColumns: [telegramMessage.id],
    name: 'MessageAnnotation_messageId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'MessageAnnotation_adminUserId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// CHAT ORDER LINK - связь чатов с заказами
// ==========================================

export const chatOrderLink = pgTable('ChatOrderLink', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  chatId: integer().notNull(),
  orderId: integer().notNull(),
  linkedByAdminId: integer().notNull(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  unique('ChatOrderLink_chatId_orderId_key').on(table.chatId, table.orderId),
  index('ChatOrderLink_chatId_idx').using('btree', table.chatId.asc().nullsLast()),
  index('ChatOrderLink_orderId_idx').using('btree', table.orderId.asc().nullsLast()),
  foreignKey({
    columns: [table.chatId],
    foreignColumns: [chat.id],
    name: 'ChatOrderLink_chatId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.orderId],
    foreignColumns: [order.id],
    name: 'ChatOrderLink_orderId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.linkedByAdminId],
    foreignColumns: [adminUser.id],
    name: 'ChatOrderLink_linkedByAdminId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// TELEGRAM SESSION SHARE - шеринг сессии конкретным админам (M:N)
// Владелец сессии открывает доступ к ней другим сотрудникам
// ==========================================

export const telegramSessionShare = pgTable('TelegramSessionShare', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  sessionId: integer().notNull(),         // ManagerTelegramSession.id — какая сессия расшарена
  sharedWithAdminId: integer().notNull(), // AdminUser.id — кому открыт доступ
  sharedByAdminId: integer().notNull(),   // AdminUser.id — владелец, который расшарил (аудит)

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  unique('TelegramSessionShare_sessionId_sharedWithAdminId_key').on(table.sessionId, table.sharedWithAdminId),
  index('TelegramSessionShare_sessionId_idx').using('btree', table.sessionId.asc().nullsLast()),
  index('TelegramSessionShare_sharedWithAdminId_idx').using('btree', table.sharedWithAdminId.asc().nullsLast()),
  foreignKey({
    columns: [table.sessionId],
    foreignColumns: [managerTelegramSession.id],
    name: 'TelegramSessionShare_sessionId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.sharedWithAdminId],
    foreignColumns: [adminUser.id],
    name: 'TelegramSessionShare_sharedWithAdminId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.sharedByAdminId],
    foreignColumns: [adminUser.id],
    name: 'TelegramSessionShare_sharedByAdminId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// ADMIN ACTIVE TELEGRAM SESSION - активный выбор сессии per смотрящий админ
// Отделяет "владение сессией" от "какую сессию сейчас использует этот админ"
// (своя или расшаренная). PK = adminUserId — один активный выбор на админа.
// ==========================================

export const adminActiveTelegramSession = pgTable('AdminActiveTelegramSession', {
  adminUserId: integer().primaryKey(),
  sessionId: integer(), // nullable: ON DELETE SET NULL при удалении сессии → резолв уходит в fallback

  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  index('AdminActiveTelegramSession_sessionId_idx').using('btree', table.sessionId.asc().nullsLast()),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'AdminActiveTelegramSession_adminUserId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.sessionId],
    foreignColumns: [managerTelegramSession.id],
    name: 'AdminActiveTelegramSession_sessionId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);
